/**
 * Surface scopes — the ladder expressed as CSS custom properties.
 *
 * ## Why custom properties rather than the theme
 *
 * A component's correct background depends on *where it is*, and that is
 * information the theme does not have. `theme.palette.background.paper` is one
 * value for the whole tree, so a card and a card-inside-a-dialog resolve
 * identically. Threading a rung down through props would mean every component
 * between the surface and the leaf has to forward it.
 *
 * CSS custom properties already solve exactly this problem: they inherit through
 * the DOM, so a component can read `var(--ds-bg)` and get whatever its nearest
 * scoped ancestor set, without knowing who that ancestor is.
 *
 * MUI's own `cssVariables: true` is not an alternative here. It exists to switch
 * a palette along *one* axis — the colour scheme — and emits one variable per
 * palette slot under a `[data-mui-color-scheme]` selector. The ladder is a
 * second, orthogonal axis: nesting depth, which varies per subtree rather than
 * per document. The two compose fine, but only DOM inheritance handles the
 * second one.
 *
 * ## Both modes share the structural rules
 *
 * Dark and light both have six named rungs. Elevation still means lighter. A
 * field still recesses one rung below its host; `page` is still the floor
 * exception (transparent fill, outline carries the affordance). Light has only
 * ~8 L* of headroom above `#E9E9E9`, so `raised` and `overlay` share white and
 * separate by shadow + border weight — Tokens Option A Light.
 */

import {
  SURFACE_LADDER,
  type SurfaceRung,
  backgroundColors,
  borderByRung,
  brandTintByRung,
  inputColors,
  lightBackgroundColors,
  lightInputColors,
  lightTextColors,
  outlineByRung,
  separatorByRung,
  tableHeaderByRung,
  textColors,
} from './colors';

/**
 * The custom properties a scope sets. Components read these rather than reading
 * tokens directly, which is what makes them context-sensitive.
 */
export const DS_VARS = {
  /** The surface's own background. */
  bg: '--ds-bg',
  /** Fill for an input hosted on this surface: one rung *down*, so it reads inset. */
  field: '--ds-field',
  /** Outline for that input. Carries the work on `page`, where there is no rung below. */
  fieldBorder: '--ds-field-border',
  /** Body text on this surface. */
  text: '--ds-text',
  /** De-emphasised text. Switches tier partway up the ladder — see `textQuiet`. */
  textQuiet: '--ds-text-quiet',
  /** Border/divider legible against this rung. */
  border: '--ds-border',
  /**
   * Row separator (~1.12:1). Lighter than `--ds-border` so table outlines stay
   * the heavier structural edge. A filled header has no underline.
   */
  separator: '--ds-separator',
  /** Hover state fill. */
  hover: '--ds-hover',
  /** Selected state fill — a resolved brand tint, not an alpha. */
  selected: '--ds-selected',
  /** The rung a DOM-nested child surface should take. Clamped at the ceiling. */
  child: '--ds-child',
  /**
   * Fill for a *control* that has to read against a container at its own level:
   * host + 2, clamped at the ceiling.
   *
   * Two rungs, not one, and the reason is that `child` is already taken by
   * containers. A switch track or an avatar sitting at +1 is the same colour as
   * a card on the same surface, so it reads as a panel rather than as something
   * you operate. +2 is the first step that distinguishes a control from a
   * container.
   */
  control: '--ds-control',
  /** The rung a *portalled* child should take: max(raised, host + 1). */
  raised: '--ds-raised',
  /**
   * Table header and row hover, derived from the host rung rather than named
   * as their own palette entries.
   *
   * Body rows read `--ds-bg`. Header is a ~1.08:1 fill that moves *away* from
   * elevation (lighter in dark, darker in light) so it reads recessed. Hover is
   * host + 2, stepping *down* at the ceiling so a nested table does not lose
   * feedback when `--ds-control` saturates.
   */
  tableHeader: '--ds-table-header',
  /**
   * Ink on a *filled* table header. The fill eats the margin `subtle` was tuned
   * for, so filled headers step up a tier (`tertiary` in dark, with `secondary`
   * at the overlay ceiling). Unfilled headers keep `--ds-text-quiet`.
   *
   * Named `--ds-table-header-ink`, not `--ds-table-header-label`: Emotion's
   * class-name generator extracts `/label:\s*([^\s;{]+)/` from serialized CSS,
   * so a custom property whose name ends in `-label` (e.g. `--foo-label:#A3A3A3`)
   * injects the hex into the generated class name. The `#` is then parsed as an
   * ID selector, the rule never matches, and Menu/Popover lose `position:
   * absolute`. See the `DS_VARS` guard in `surfaceScopes.test.ts`.
   */
  tableHeaderLabel: '--ds-table-header-ink',
  tableRowHover: '--ds-table-row-hover',
} as const;

export interface SurfaceScope {
  bg: string;
  field: string;
  fieldBorder: string;
  text: string;
  textQuiet: string;
  border: string;
  separator: string;
  hover: string;
  selected: string;
  child: string;
  control: string;
  raised: string;
  tableHeader: string;
  tableHeaderLabel: string;
  tableRowHover: string;
}

/**
 * Header and hover for a table mounted on `rung`. Body rows are the host fill
 * (`--ds-bg`).
 *
 * The header fill is ~1.08:1 against the host and moves *away* from the
 * elevation direction (lighter in dark, darker in light) so it reads as
 * recessed. A filled header is the edge — it does not also take a bottom rule
 * (sticky headers are the exception; they borrow `--ds-separator`).
 *
 * Hover is host + 2. At the ceiling `nextRung` saturates, so hover would equal
 * the row and a nested table would silently lose its feedback. Losing hover is
 * an interaction regression rather than a cosmetic one, so when the ladder
 * runs out the band steps *down* instead.
 */
function tableBands(rung: SurfaceRung) {
  const row = backgroundColors[rung];
  const down = backgroundColors[previousRung(rung)];
  const hoverUp = backgroundColors[nextRung(nextRung(rung))];
  const saturated = hoverUp === row;
  return {
    tableHeader: tableHeaderByRung[rung],
    // Filled-header labels: tertiary clears 4.5:1 on every rung except overlay,
    // which needs secondary.
    tableHeaderLabel: rung === 'overlay' ? textColors.secondary : textColors.tertiary,
    tableRowHover: saturated ? down : hoverUp,
  };
}

/** Next rung up, saturating at the top. */
export function nextRung(rung: SurfaceRung): SurfaceRung {
  const i = SURFACE_LADDER.indexOf(rung);
  return SURFACE_LADDER[Math.min(i + 1, SURFACE_LADDER.length - 1)];
}

/** Previous rung down, saturating at the bottom. */
export function previousRung(rung: SurfaceRung): SurfaceRung {
  const i = SURFACE_LADDER.indexOf(rung);
  return SURFACE_LADDER[Math.max(i - 1, 0)];
}

/**
 * The rung a portalled element should use: `max(raised, host + 1)`.
 *
 * A portalled element is reparented to `document.body`, so it cannot inherit a
 * rung through the DOM. `raised` is the floor because a tooltip over the app
 * shell still has to read as floating; `host + 1` takes over once the host is
 * already high enough that `raised` would not clear it.
 */
export function portalRung(host: SurfaceRung): SurfaceRung {
  const floor = SURFACE_LADDER.indexOf('raised');
  const above = SURFACE_LADDER.indexOf(nextRung(host));
  return SURFACE_LADDER[Math.max(floor, above)];
}

/**
 * Quiet text expires partway up the ladder.
 *
 * `text.subtle` clears 4.5:1 on `page`, `primary` and `widget` and drops to
 * 4.25:1 on `surface`. Rather than leaving that as a rule people have to
 * remember, the scope swaps the tier — a component that reads
 * `var(--ds-text-quiet)` gets a compliant colour wherever it is mounted.
 * Pinned by `textRamp.contrast.test.ts`.
 */
function quietText(rung: SurfaceRung): string {
  const subtleHolds: readonly SurfaceRung[] = ['page', 'primary', 'widget'];
  return subtleHolds.includes(rung) ? textColors.subtle : textColors.tertiary;
}

function darkScope(rung: SurfaceRung): SurfaceScope {
  const onPage = rung === 'page';
  return {
    bg: backgroundColors[rung],
    // A field recesses one rung below its host. On `page` there is nothing
    // below, so it goes transparent and the outline does the work instead.
    field: onPage ? 'transparent' : backgroundColors[previousRung(rung)],
    // On `page` the outline is the only thing marking the field out, so it takes
    // the heavier weight; elsewhere the recessed fill already reads.
    fieldBorder: onPage ? outlineByRung[rung] : inputColors.border,
    text: textColors.primary,
    textQuiet: quietText(rung),
    border: borderByRung[rung],
    separator: separatorByRung[rung],
    hover: 'rgba(255, 255, 255, 0.06)',
    selected: brandTintByRung[rung],
    child: backgroundColors[nextRung(rung)],
    control: backgroundColors[nextRung(nextRung(rung))],
    raised: backgroundColors[portalRung(rung)],
    ...tableBands(rung),
  };
}

/**
 * Light field fill for a host rung — Tokens Option A Light §03.
 *
 * Strict host−1 through `surface`. `raised` and `overlay` share a white host,
 * so they share one recessed fill (`primary`) rather than almost-white steps
 * that would not read.
 */
function lightField(rung: SurfaceRung): string {
  if (rung === 'page') return 'transparent';
  if (rung === 'raised' || rung === 'overlay') return lightBackgroundColors.primary;
  return lightBackgroundColors[previousRung(rung)];
}

function lightTableHeader(rung: SurfaceRung): string {
  // Recessed means darker in both themes. On `page` there is nothing below, so
  // step under the ladder; at the white ceiling reuse the primary recess.
  if (rung === 'page') return '#E2E2E2';
  if (rung === 'raised' || rung === 'overlay') return lightBackgroundColors.primary;
  return lightBackgroundColors[previousRung(rung)];
}

function lightControl(rung: SurfaceRung): string {
  // Dark lifts a control +2; light has no headroom above white, so a control
  // steps *down* two rungs into a recessed grey instead.
  const stepped = lightBackgroundColors[previousRung(previousRung(rung))];
  if (stepped !== lightBackgroundColors[rung]) return stepped;
  return '#E2E2E2';
}

function lightScope(rung: SurfaceRung): SurfaceScope {
  return {
    bg: lightBackgroundColors[rung],
    field: lightField(rung),
    // Floor exception text and the default hairline are both `#D4D4D4`.
    fieldBorder: lightInputColors.border,
    text: lightTextColors.primary,
    // `subtle` clears AA from `primary` up; on `page` (#E9E9E9) it drops to
    // ~4.2:1, so swap to `tertiary` for the same reason dark swaps at `surface`.
    textQuiet: rung === 'page' ? lightTextColors.tertiary : lightTextColors.subtle,
    // `raised` uses the subtler ring; overlay keeps the default hairline.
    border: rung === 'raised' ? lightBackgroundColors.borderSubtle : lightBackgroundColors.border,
    separator: rung === 'page' ? '#DCDCDC' : lightBackgroundColors.tableDivider,
    hover: 'rgba(0, 0, 0, 0.04)',
    selected: 'rgba(0, 137, 123, 0.10)',
    child: lightBackgroundColors[nextRung(rung)],
    control: lightControl(rung),
    raised: lightBackgroundColors[portalRung(rung)],
    tableHeader: lightTableHeader(rung),
    tableHeaderLabel: lightTextColors.tertiary,
    // Hover/selected rows go to white — the only step left that clears zebra.
    tableRowHover: '#FFFFFF',
  };
}

function buildScopes(build: (rung: SurfaceRung) => SurfaceScope) {
  return Object.fromEntries(SURFACE_LADDER.map((rung) => [rung, build(rung)])) as Record<
    SurfaceRung,
    SurfaceScope
  >;
}

export const surfaceScopes = {
  dark: buildScopes(darkScope),
  light: buildScopes(lightScope),
} as const satisfies Record<'dark' | 'light', Record<SurfaceRung, SurfaceScope>>;

/** Turn a scope into the custom-property declarations for a style object. */
export function scopeToCssVars(scope: SurfaceScope): Record<string, string> {
  return {
    [DS_VARS.bg]: scope.bg,
    [DS_VARS.field]: scope.field,
    [DS_VARS.fieldBorder]: scope.fieldBorder,
    [DS_VARS.text]: scope.text,
    [DS_VARS.textQuiet]: scope.textQuiet,
    [DS_VARS.border]: scope.border,
    [DS_VARS.separator]: scope.separator,
    [DS_VARS.hover]: scope.hover,
    [DS_VARS.selected]: scope.selected,
    [DS_VARS.child]: scope.child,
    [DS_VARS.control]: scope.control,
    [DS_VARS.raised]: scope.raised,
    [DS_VARS.tableHeader]: scope.tableHeader,
    [DS_VARS.tableHeaderLabel]: scope.tableHeaderLabel,
    [DS_VARS.tableRowHover]: scope.tableRowHover,
  };
}

/**
 * `var(--ds-x, fallback)`, so a component works even when it is rendered outside
 * any scope — a bare `<Chip />` in a test, or a portalled element that escaped
 * its scope. Without a fallback those render with no background at all.
 */
export function dsVar(name: keyof typeof DS_VARS, fallback: string): string {
  return `var(${DS_VARS[name]}, ${fallback})`;
}
